import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  Heart, MessageCircle, Share2, Eye, Play, Plus, Crown, Verified,
  Video, Image as ImageIcon, Globe2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

function MediaGrid({ videos, images, postId }) {
  const v = videos || [];
  const i = images || [];
  const allEmbed = [...v.map(x => ({ ...x, kind: "video" })), ...i.map(x => ({ ...x, kind: "image" }))];
  if (allEmbed.length === 0) return null;
  const cols = allEmbed.length === 1 ? "grid-cols-1"
    : allEmbed.length === 2 ? "grid-cols-2"
    : allEmbed.length === 3 ? "grid-cols-2"
    : "grid-cols-2";
  return (
    <div className={`grid ${cols} gap-px bg-gray-300 border border-gray-300 mt-5`} data-testid={`media-grid-${postId}`}>
      {allEmbed.map((m, idx) => (
        <div key={`${postId}-${idx}`} className={`relative bg-black ${allEmbed.length === 3 && idx === 0 ? "row-span-2" : ""}`}>
          {m.kind === "video" ? (
            <div className="aspect-video relative">
              <iframe
                src={m.url}
                title={m.title || `video-${idx}`}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid={`video-${postId}-${idx}`}
              />
              {m.title && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2 pointer-events-none">
                  <div className="mono text-[10px] tracking-wider text-white uppercase">{m.title}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="aspect-video relative">
              <img src={m.url} alt={m.alt || "media"} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AuthorAvatar({ p }) {
  const seed = (p.author_handle || p.author_name || "?")[0].toUpperCase();
  const hue = (seed.charCodeAt(0) * 5) % 360;
  return (
    <div
      className="w-12 h-12 text-white flex items-center justify-center font-bold shrink-0"
      style={{ background: `hsl(${hue}, 35%, 22%)` }}
    >
      {(p.author_name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
    </div>
  );
}

function PostCard({ p, onLike }) {
  const [likes, setLikes] = useState(p.likes || 0);
  const [liked, setLiked] = useState(false);

  const like = async () => {
    if (liked) return;
    setLiked(true);
    setLikes(l => l + 1);
    try { await onLike(p.id); } catch (err) { console.error("Like failed:", err); }
  };

  const typeChip = {
    mass_opportunity: { label: "Mass Opportunity", color: "bg-[#FACC15] text-[#064E3B]" },
    video_essay: { label: "Video Essay", color: "bg-[#064E3B] text-white" },
    announcement: { label: "Announcement", color: "bg-[#D97706] text-white" },
  }[p.type] || { label: "Post", color: "bg-gray-200 text-gray-800" };

  return (
    <article className="border border-gray-300 bg-white" data-testid={`social-post-${p.id}`}>
      <div className="p-6 flex items-start gap-4">
        <AuthorAvatar p={p} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/exchange/member/${p.author_handle}`} className="display-black text-base uppercase text-[#064E3B] hover:underline">
              {p.author_name}
            </Link>
            <Verified className="w-4 h-4 text-[#064E3B]" />
            <span className={`mono text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 ${typeChip.color}`}>{typeChip.label}</span>
          </div>
          <div className="mono text-[11px] tracking-wider text-gray-500 uppercase mt-0.5">
            {p.author_title} · {p.author_company}
          </div>
        </div>
        <div className="text-right mono text-[10px] tracking-wider text-gray-500 uppercase">
          {p.created_at?.slice(0, 10)}
        </div>
      </div>

      <div className="px-6 pb-2">
        <h2 className="display-black text-2xl lg:text-3xl uppercase text-[#064E3B] leading-[1.05]">{p.headline}</h2>
        <p className="text-[15px] text-gray-800 mt-4 leading-relaxed whitespace-pre-wrap">{p.body}</p>
        {p.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {p.tags.map(t => (
              <Link key={t} to={`/availableme?tag=${t}`} className="mono text-[10px] tracking-wider uppercase px-2 py-1 border border-gray-300 text-gray-700 hover:border-[#064E3B] hover:text-[#064E3B]">
                #{t}
              </Link>
            ))}
          </div>
        )}
      </div>

      <MediaGrid videos={p.videos} images={p.images} postId={p.id} />

      <div className="px-6 py-4 border-t border-gray-200 flex items-center gap-6 mono text-[11px] tracking-wider text-gray-600 uppercase">
        <button onClick={like} className={`inline-flex items-center gap-2 hover:text-[#D97706] transition-colors ${liked ? "text-[#D97706]" : ""}`} data-testid={`like-${p.id}`}>
          <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> {likes.toLocaleString()}
        </button>
        <span className="inline-flex items-center gap-2"><MessageCircle className="w-4 h-4" /> {p.comments?.toLocaleString() || 0}</span>
        <span className="inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> {p.shares?.toLocaleString() || 0}</span>
        <span className="inline-flex items-center gap-2 ml-auto"><Eye className="w-4 h-4" /> {p.views?.toLocaleString() || 0}</span>
      </div>
    </article>
  );
}

export default function AvailableMe() {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => { api.get("/social/stats").then(r => setStats(r.data)); }, []);

  useEffect(() => {
    const params = typeFilter ? { type: typeFilter } : {};
    api.get("/social/feed", { params }).then(r => setPosts(r.data));
  }, [typeFilter]);

  const like = async (id) => {
    await api.post(`/social/post/${id}/like`);
  };

  return (
    <>
      {/* Hero */}
      <section className="bg-[#064E3B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="availableme-hero">
          <div className="federal-stamp" style={{ borderColor: "#FACC15", color: "#FACC15", background: "rgba(250,204,21,0.08)" }}>
            ★ AVAILABLEME · VIDEO-FIRST SOCIAL FOR OPERATORS
          </div>
          <h1 className="display-black text-5xl lg:text-7xl uppercase mt-6 leading-[0.95] tracking-tighter max-w-5xl">
            Not little posts.<br /><span className="text-[#FACC15]">Whole opportunities.</span>
          </h1>
          <p className="text-lg text-white/85 mt-6 max-w-3xl">
            AvailableMe is what happens when LinkedIn meets YouTube meets the Available Exchange. Every post can carry four videos, four images and a real deal. Watch builders broadcast their ambition — and respond.
          </p>

          {stats && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/15" data-testid="availableme-stats">
              {[
                { v: stats.posts, l: "Posts" },
                { v: stats.videos, l: "Videos" },
                { v: stats.views.toLocaleString(), l: "Views" },
                { v: stats.likes.toLocaleString(), l: "Likes" },
                { v: stats.creators, l: "Creators" },
              ].map(s => (
                <div key={s.l} className="bg-[#064E3B] p-5 text-center">
                  <div className="display-black text-2xl lg:text-3xl text-[#FACC15]">{s.v}</div>
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-white/70 mt-2">{s.l}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/availableme/compose" className="btn-signal" data-testid="compose-cta"><Plus className="w-4 h-4" /> Compose Post</Link>
            <Link to="/exchange" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }}>The Exchange</Link>
          </div>
        </div>
      </section>

      {/* Filter strip */}
      <section className="border-b border-gray-300 bg-white sticky top-16 z-20" data-testid="availableme-toolbar">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap gap-3 items-center">
          {[
            { v: "", label: "All", icon: Globe2 },
            { v: "mass_opportunity", label: "Mass Opportunities", icon: Sparkles },
            { v: "video_essay", label: "Video Essays", icon: Video },
            { v: "announcement", label: "Announcements", icon: ImageIcon },
          ].map(t => {
            const Icon = t.icon;
            const active = typeFilter === t.v;
            return (
              <button
                key={t.v || "all"}
                onClick={() => setTypeFilter(t.v)}
                className={`mono text-[11px] tracking-[0.22em] uppercase px-4 py-2 border inline-flex items-center gap-2 ${active ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white text-[#064E3B] border-gray-300 hover:border-[#064E3B]"}`}
                data-testid={`filter-${t.v || "all"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Feed + Sidebar */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3 space-y-6" data-testid="availableme-feed">
            {posts.map(p => <PostCard key={p.id} p={p} onLike={like} />)}
            {posts.length === 0 && <div className="border border-gray-300 p-8 text-center text-gray-500">No posts in this filter.</div>}
          </div>

          <aside className="space-y-6">
            <div className="border border-[#064E3B] bg-[#064E3B] text-white p-6" data-testid="why-card">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#FACC15]">// Why AvailableMe</div>
              <h3 className="display-black text-xl uppercase mt-2">Every post is an interactive page.</h3>
              <ul className="mt-4 space-y-3 text-sm text-white/85">
                <li>• Up to 4 videos per post</li>
                <li>• Mass-opportunity callouts pinned in feed</li>
                <li>• Operator-grade audience, no influencer noise</li>
                <li>• Connects directly to the Exchange</li>
              </ul>
              <Link to="/availableme/compose" className="btn-ghost w-full mt-5 justify-center" style={{ color: "#FACC15", borderColor: "#FACC15" }}>Compose now</Link>
            </div>

            <div className="border border-gray-300 p-6 bg-gray-50">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Trending Tags</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["acquisition", "maritime", "aviation", "capital", "ma", "africa", "honor-pay", "available-exchange", "roll-up"].map(t => (
                  <Link key={t} to={`/availableme?tag=${t}`} className="mono text-[10px] tracking-wider uppercase px-2 py-1 border border-gray-300 hover:border-[#064E3B] hover:text-[#064E3B]">
                    #{t}
                  </Link>
                ))}
              </div>
            </div>

            <div className="border border-gray-300 p-6">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-[#FACC15]" />
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#064E3B]">// Creator Studio</div>
              </div>
              <p className="text-sm text-gray-800 mt-3">Multi-video pages. Branded canvases. Sponsor takeovers. The studio is rolling out to verified creators.</p>
              <button disabled className="btn-ghost w-full mt-5 justify-center opacity-60 cursor-not-allowed" data-testid="studio-waitlist">Waitlist</button>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
